import React, { useState } from 'react';
import axios from 'axios';
import { BarChart3, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_COLORS = [
  'from-purple-500 to-pink-500',
  'from-cyan-500 to-blue-500',
  'from-orange-500 to-red-500',
  'from-green-500 to-emerald-500',
  'from-violet-500 to-indigo-500',
  'from-yellow-500 to-amber-500',
];

/* Brief LOT6.1 — support multi-choix (parité sondages communauté) :
   • allow_multiple → cases à cocher, sélection cumulable, re-clic = retrait
   • single → re-clic remplace ou retire le vote (plus de verrou "Already voted")
   Chaque clic envoie le set complet {option_ids:[...]} au backend. */
const FeedPollCard = ({ postId, poll, onVote }) => {
  const [voting, setVoting] = useState(false);
  const [localPoll, setLocalPoll] = useState(poll);

  if (!localPoll) return null;
  const options = Array.isArray(localPoll.options) ? localPoll.options : [];
  const question = localPoll.question || 'Sondage';
  const allowMultiple = !!localPoll.allow_multiple;

  const totalVotes = localPoll.total_votes || 0;
  const userVotes = Array.isArray(localPoll.user_votes)
    ? localPoll.user_votes
    : (localPoll.user_vote ? [localPoll.user_vote] : []);
  const hasVoted = userVotes.length > 0;

  const handleVote = async (optionId) => {
    if (voting) return;
    setVoting(true);
    try {
      let newVotes;
      if (allowMultiple) {
        newVotes = userVotes.includes(optionId)
          ? userVotes.filter((x) => x !== optionId)
          : [...userVotes, optionId];
      } else {
        newVotes = userVotes.includes(optionId) ? [] : [optionId];
      }
      const { data } = await axios.post(`${API}/feed-polls/${postId}/vote`, { option_ids: newVotes }, { withCredentials: true });
      if (data.poll) setLocalPoll(data.poll);
      if (onVote) onVote();
    } catch (err) {
      console.error('Vote error:', err);
    } finally {
      setVoting(false);
    }
  };

  return (
    <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl" data-testid={`feed-poll-${postId}`}>
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="h-5 w-5 text-purple-500" />
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">{question}</h4>
      </div>
      {allowMultiple && (
        <span
          className="inline-flex items-center gap-1 mb-2 text-[11px] font-medium text-purple-700 bg-purple-100 dark:bg-purple-900/40 dark:text-purple-300 px-2 py-0.5 rounded-full"
          data-testid={`feed-poll-multi-badge-${postId}`}
        >
          <Check className="h-3 w-3" /> Plusieurs choix possibles
        </span>
      )}

      <div className="space-y-2 mt-2">
        {options.map((opt, idx) => {
          const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
          const isSelected = userVotes.includes(opt.option_id);
          const colorGradient = POLL_COLORS[idx % POLL_COLORS.length];

          return (
            <button
              key={opt.option_id}
              onClick={() => handleVote(opt.option_id)}
              disabled={voting}
              className={`relative w-full text-left rounded-xl overflow-hidden transition-all cursor-pointer hover:shadow-md ${
                isSelected ? 'ring-2 ring-purple-500' : ''
              }`}
              data-testid={`poll-option-${opt.option_id}`}
            >
              <div className="relative bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl">
                {hasVoted && (
                  <div
                    className={`absolute inset-y-0 left-0 bg-gradient-to-r ${colorGradient} opacity-20 rounded-xl transition-all duration-700`}
                    style={{ width: `${pct}%` }}
                  />
                )}
                <div className="relative flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 ${allowMultiple ? 'rounded' : 'rounded-full'} border-2 flex items-center justify-center shrink-0 ${isSelected ? 'border-purple-500 bg-purple-500' : 'border-gray-300 dark:border-gray-500'}`}>
                      {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
                    </div>
                    <span className={`text-sm ${isSelected ? 'font-semibold text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300'}`}>
                      {opt.text}
                    </span>
                  </div>
                  {hasVoted && (
                    <span className={`text-sm font-bold ${isSelected ? 'text-purple-600' : 'text-gray-500 dark:text-gray-400'}`}>
                      {pct}%
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 mt-3 text-center">
        {totalVotes} vote{totalVotes !== 1 ? 's' : ''}
      </p>
    </div>
  );
};

export default FeedPollCard;
